import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map} from "rxjs/operators";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {PartnerShopOrderDgSearchResults} from "../models/datagrid-search-results/partner-shop-order.dg-search-results.model";
import {PartnerShopOrder} from "../models/partner-shop-order.model";

/**
 * Serwis warstwy danych obsługujący zamówienia ze sklepów partnerów.
 * Odpowiada za zarządzanie, wyszukiwanie i podgląd transakcji zrealizowanych
 * w ramach dedykowanych sklepów partnerskich.
 */
@Injectable({
  providedIn: 'root'
})
export class PartnerShopOrdersService implements DataGridServiceInterface<Observable<PartnerShopOrderDgSearchResults>> {

  constructor(private apiService: ApiService, public commonUtils: CommonUtils) {
  }

  /**
   * Pobiera paginowaną listę zamówień partnerskich w celu zasilenia widoku tabelarycznego.
   *
   * @param limit Maksymalna liczba rekordów na stronę.
   * @param offset Przesunięcie wyników względem początku zbioru danych.
   * @param sortOrder Kierunek sortowania (np. 'ASC', 'DESC').
   * @param orderBy Nazwa kolumny, według której aplikowane jest sortowanie.
   * @param showDeletedObjects Flaga określająca widoczność usuniętych/anulowanych zamówień.
   * @returns Observable emitujący ustandaryzowany obiekt z wynikami i metadanymi dla tabeli.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<PartnerShopOrderDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partner-shop-orders/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        const partnerShopOrderDgSearchResults = new PartnerShopOrderDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new PartnerShopOrder(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
        return partnerShopOrderDgSearchResults;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wykonuje filtrowanie i wyszukiwanie zamówień partnerskich na podstawie
   * zaawansowanych kryteriów.
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa zawierająca zdefiniowane filtry wyszukiwania.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Nazwa kolumny sortującej.
   * @param showDeletedObjects Flaga widoczności usuniętych rekordów.
   * @returns Observable emitujący przefiltrowaną listę zamówień dla DataGrid.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<PartnerShopOrderDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>('/dashboard/partner-shop-orders/search', data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response.results);
        return new PartnerShopOrderDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new PartnerShopOrder(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pełne, szczegółowe dane pojedynczego zamówienia partnerskiego.
   *
   * @param uuid Unikalny identyfikator zamówienia partnerskiego.
   * @returns Observable emitujący szczegółowy obiekt zamówienia.
   */
  getDetails(uuid: string): Observable<PartnerShopOrder> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partner-shop-orders/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<PartnerShopOrder>(new PartnerShopOrder(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
